import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { OtherChargesService } from '../../../services/other-charges.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { CurrencyPipe } from '@angular/common';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { EActionTypes, EUserSecurityFunctionID, EToaster } from 'src/app/Shared/enum';
import { TooltipModule } from "@syncfusion/ej2-angular-popups";
import { GeneralService } from "src/app/services/general.service";
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { CurrencyFormatterDirective } from 'src/app/Shared/currencyFormatter.directive';

@Component({
  selector: 'app-ops-other-charges',
  standalone: true,
  imports: [
    SYNCFUSION_MODULES,
    ANGULAR_MODULES,
    OperationsComponent,
    TopBarComponent,
    UserPermissionHandlerDirective,
    TooltipModule,
    CurrencyFormatterDirective
  ],
  providers: [FilterService, CurrencyPipe],
  templateUrl: './ops-other-charges.html',
  styleUrl: './ops-other-charges.scss'
})
export class OpsOtherCharges implements OnInit, OnDestroy {
  isCollapsed = true;
  @ViewChild('gridOtherCharges', { static: false }) public gridOtherCharges!: GridComponent;

  otherChargesForm!: FormGroup;
  otherItemCategories: { id: number; name: string }[] = [];
  data: any[] = [];
  allData: any[] = [];
  activeTab: 'all' | 'active' | 'inactive' = 'all';
  selectedChargeId: number | null = null;
  private destroy$ = new Subject<void>();
  formInitialValue: any = null;

  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.OtherCharges, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.OtherCharges, Action: EActionTypes.CREATE };
  deletePermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.OtherCharges, Action: EActionTypes.DELETE };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.OtherCharges, Action: EActionTypes.EDIT };

  constructor(
    private fb: FormBuilder,
    private otherChargesService: OtherChargesService,
    private toastr: ToastrService,
    private currencyPipe: CurrencyPipe,
    private generalService: GeneralService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private wordReplacePipe: WordReplacePipe,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadInitialData();
  }

  get editRight(): boolean {
    return this.generalService.getUserPermissionsForModule(EUserSecurityFunctionID.OtherCharges).EditRight;
  }
  get deleteRight(): boolean {
    return this.generalService.getUserPermissionsForModule(EUserSecurityFunctionID.OtherCharges).DeleteRight;
  }

  private initForm(): void {
    this.otherChargesForm = this.fb.group({
      Description: ['', [Validators.maxLength(25), Validators.required]],
      OtherItemCategory: [null, Validators.required],
      Recurring: [false],
      DailyCharge: [null],
      WeeklyCharge: [null],
      MonthlyCharge: [null],
      InActive: [false],
      Priority: [null]
    });
  }

  private loadInitialData(): void {
    forkJoin({
      categories: this.otherChargesService.getAllRevenueCategories(),
      charges: this.otherChargesService.getOtherCharges()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ categories, charges }) => {
          this.otherItemCategories = (categories || []).map((c: any) => ({
            id: c.RevSeqCategoryId,
            name: c.RevTxtCode
          }));

          this.allData = (charges || []).map((c: any) => ({
            ...c,
            RevenueCategory: this.otherItemCategories.find(cat => cat.id === c.OtherItemCategory)?.name || ''
          }));

          this.applyFilter(this.activeTab);
        },
        error: () => this.toastr.error($localize`Error loading data`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig)
      });
  }

  trimAllTextFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.trimAllTextFields(control);
      } else if (typeof control?.value === 'string') {
        control.setValue(control.value.trim());
      }
    });
  }

  allowOnlyDigits(event: Event, maxDigits: number) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (maxDigits && value.length > maxDigits) value = value.substring(0, maxDigits);
    input.value = value;

    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.otherChargesForm.get(controlName)?.setValue(value ? Number(value) : null);
    }
  }

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value ?? 0, '$', 'symbol', '1.2-2') || '$0.00';
  }

  saveOtherCharge(): void {
    this.trimAllTextFields(this.otherChargesForm);

    if (this.otherChargesForm.invalid) {
      this.otherChargesForm.markAllAsTouched();
      this.toastr.warning($localize`Please fill all required fields`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    Swal.fire({
      title: $localize`Are you sure?`,
      text: this.selectedChargeId
        ? $localize`Do you want to update this other charge?`
        : $localize`Do you want to create this other charge?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, save it`,
      cancelButtonText: $localize`Cancel`
    }).then(result => {
      if (result.isConfirmed) {
        const payload = {
          ...this.otherChargesForm.value,
          OtherRentalChargeId: this.selectedChargeId || 0
        };

        this.otherChargesService.saveOtherCharges(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res?.Success) {
                this.toastr.success(
                  this.selectedChargeId
                    ? $localize`Other charge updated successfully`
                    : $localize`Other charge saved successfully`,
                  $localize`Success`,
                  this.sharedHTMLSettings.ToasterConfig
                );
                this.loadInitialData();
                this.resetForm();
                this.getModalInstance('otherChargesModal')?.hide();
              } else {
                this.toastr.warning(
                  res?.Message || $localize`Failed to save other charge`,
                  $localize`Validation`,
                  this.sharedHTMLSettings.ToasterConfig
                );
              }
            },
            error: () =>
              this.toastr.error(
                $localize`Error saving other charge`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              )
          });
      }
    });
  }

  deleteOtherCharge(otherChargeId: number): void {
    if (!this.deleteRight) {
      this.toastr.info(
        EToaster.MsgInfoNoRightToDelete + $localize`:@@OtherCharge:Other Charge`,
        EToaster.TitleInfo,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const selectedCharge = this.allData.find(ele => ele.OtherRentalChargeId === otherChargeId);
    if (!selectedCharge) {
      this.toastr.error(
        $localize`Selected Other Charge not found`,
        $localize`Error`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    Swal.fire({
      title: $localize`Are you sure?`,
      text: $localize`This Other Charge will be deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, delete it!`,
      cancelButtonText: $localize`Cancel`
    }).then((result) => {
      if (result.isConfirmed) {
        this.otherChargesService.deleteOtherCharge(otherChargeId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res?.Success) {
                this.toastr.success(
                  $localize`Other Charge deleted successfully`,
                  $localize`Success`,
                  this.sharedHTMLSettings.ToasterConfig
                );
                this.loadInitialData();
              } else {
                const errorMessage = this.wordReplacePipe.transform(res?.Message);
                this.toastr.error(
                  errorMessage || $localize`Other Charge delete was unsuccessful`,
                  $localize`Error`,
                  this.sharedHTMLSettings.ToasterConfig
                );
              }
            },
            error: () => {
              this.toastr.error(
                $localize`Delete was unsuccessful`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              );
            }
          });
      }
    });
  }

  get isSaveDisabled(): boolean {
    if (this.otherChargesForm.invalid) return true;
    if (!this.selectedChargeId) return false;
    return this.otherChargesForm.pristine;
  }

  openNewOtherChargeModal(): void {
    this.resetForm();
    this.formInitialValue = null;
    this.getModalInstance('otherChargesModal')?.show();
  }

  cancelOtherCharge(): void {
    if (this.otherChargesForm.dirty) {
      Swal.fire({
        title: $localize`:@@Confirm:Confirm`,
        text: this.selectedChargeId
          ? $localize`:@@ConfirmDiscardEdit:Discard changes to this Other Charge?`
          : $localize`:@@ConfirmDiscardNew:Discard new Other Charge?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: $localize`:@@Yes:Yes`,
        cancelButtonText: $localize`:@@No:No`
      }).then(result => {
        if (result.isConfirmed) this.closeOtherChargeModal();
      });
    } else {
      this.closeOtherChargeModal();
    }
  }

  private closeOtherChargeModal(): void {
    this.getModalInstance('otherChargesModal')?.hide();
    this.resetForm();
  }

  onRowSelected(args: any): void {
    if (!this.editRight) {
      this.toastr.info(
        EToaster.MsgInfoNoRightToEdit + $localize`:@@OtherCharge:Other Charge`,
        EToaster.TitleInfo,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const rowData = args.data;
    if (!rowData) return;

    this.selectedChargeId = rowData.OtherRentalChargeId;
    this.otherChargesForm.patchValue({
      Description: rowData.Description,
      OtherItemCategory: rowData.OtherItemCategory,
      Recurring: rowData.Recurring,
      DailyCharge: rowData.DailyCharge,
      WeeklyCharge: rowData.WeeklyCharge,
      MonthlyCharge: rowData.MonthlyCharge,
      InActive: rowData.InActive,
      Priority: rowData.Priority
    });

    ['DailyCharge', 'WeeklyCharge', 'MonthlyCharge'].forEach(field => {
      const inputEl = document.querySelector<HTMLInputElement>(`input[formControlName="${field}"]`);
      if (inputEl) inputEl.value = this.formatCurrency(this.otherChargesForm.get(field)?.value);
    });

    this.formInitialValue = this.otherChargesForm.getRawValue();
    this.otherChargesForm.markAsPristine();
    this.getModalInstance('otherChargesModal')?.show();
  }

  private resetForm(): void {
    this.otherChargesForm.reset({
      Description: '',
      OtherItemCategory: null,
      Recurring: false,
      DailyCharge: null,
      WeeklyCharge: null,
      MonthlyCharge: null,
      InActive: false,
      Priority: null
    });
    this.otherChargesForm.markAsPristine();
    this.selectedChargeId = null;
  }

  applyFilter(tab: 'all' | 'active' | 'inactive'): void {
    this.activeTab = tab;
    this.data =
      tab === 'active' ? this.allData.filter(i => !i.InActive) :
        tab === 'inactive' ? this.allData.filter(i => i.InActive) :
          [...this.allData];
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public created(args1) {
    this.gridOtherCharges.element.querySelectorAll('.e-filterdiv')[this.gridOtherCharges.getColumnIndexByField('Recurring')].classList.add('e-hide');
    this.gridOtherCharges.element.querySelectorAll('.e-filterdiv')[this.gridOtherCharges.getColumnIndexByField('DailyCharge')].classList.add('e-hide');
    this.gridOtherCharges.element.querySelectorAll('.e-filterdiv')[this.gridOtherCharges.getColumnIndexByField('WeeklyCharge')].classList.add('e-hide');
    this.gridOtherCharges.element.querySelectorAll('.e-filterdiv')[this.gridOtherCharges.getColumnIndexByField('MonthlyCharge')].classList.add('e-hide');
    this.gridOtherCharges.element.querySelectorAll('.e-filterdiv')[this.gridOtherCharges.getColumnIndexByField('InActive')].classList.add('e-hide');
    this.gridOtherCharges.element.querySelectorAll('.e-filterdiv')[this.gridOtherCharges.getColumnIndexByField('Priority')].classList.add('e-hide');
    this.gridOtherCharges.element.querySelectorAll('.e-filterdiv')[this.gridOtherCharges.getColumnIndexByField('Delete')].classList.add('e-hide');

    this.gridOtherCharges.selectionSettings = { checkboxMode: 'ResetOnRowClick', mode: 'Row', type: 'Single', enableSimpleMultiRowSelection: false, checkboxOnly: false };
    this.gridOtherCharges.filterSettings = { mode: 'Immediate', immediateModeDelay: 100 };
  }

  onBeforeOpen(args: any): void {
    if (!args?.target?.dataset?.content) args.cancel = true;
  }

  private getModalInstance(id: string): any {
    const modalEl = document.getElementById(id);
    if (!modalEl) return null;
    return window['bootstrap'].Modal.getInstance(modalEl) || new window['bootstrap'].Modal(modalEl);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
